import { Box, Image, Text } from '@chakra-ui/react';
import i18next from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { languageMapping } from '../../../i18n/config';
import belowIcon from '../images/below.png';

// 公告內容為後台注入的 HTML 字串，Tailwind 的 preflight 會重置標籤預設樣式，
// 因此這裡補回常用標籤的樣式，讓注入的 HTML 能完整正常顯示
// 收合狀態下的最大高度（約 3 行，需與 richTextStyle 的 lineHeight 對應）
const COLLAPSED_MAX_HEIGHT = '4.8em';

const richTextStyle = {
  wordBreak: 'break-word',
  lineHeight: '1.6',
  '& img, & video, & iframe': {
    maxWidth: '100%',
    height: 'auto'
  },
  '& p': { margin: '0.5em 0' },
  '& a': { color: '#A4F0FF', textDecoration: 'underline' },
  '& ul, & ol': { paddingLeft: '1.5em', margin: '0.5em 0' },
  '& ul': { listStyleType: 'disc' },
  '& ol': { listStyleType: 'decimal' },
  '& li': { listStyle: 'inherit' },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontWeight: 'bold',
    margin: '0.5em 0'
  },
  '& h1': { fontSize: '1.6em' },
  '& h2': { fontSize: '1.4em' },
  '& h3': { fontSize: '1.2em' },
  '& strong, & b': { fontWeight: 'bold' },
  '& em, & i': { fontStyle: 'italic' },
  '& hr': { margin: '0.5em 0', borderTop: '1px solid #A4F0FF' },
  '& table': { width: '100%', display: 'block', overflowX: 'auto' },
  '& td, & th': { border: '1px solid #A4F0FF', padding: '0.25em 0.5em' },
  '& pre': { whiteSpace: 'pre-wrap' }
};

// 將公告列表中的每個公告根據當前語言進行處理，選擇合適的語言版本
const getList = (announcementList) => {
  if (!announcementList || announcementList.length === 0) return [];

  const processedArrayData = announcementList.map((item) => ({
    ...item,
    currentLang:
      item.news_language.find(
        (langItem) => langItem === languageMapping(i18next.language)
      ) || item.news_language?.[0]
  }));

  return processedArrayData;
};

/**
 * @description 公告內容組件，根據傳入的公告列表顯示相關信息
 * @param {Array} announcementList - 包含公告信息的列表
 * @return {JSX.Element}
 */
const AnnouncementContent = ({ reduxAnnouncementList }) => {
  const { t } = useTranslation();
  // 本地狀態管理公告列表、展開的公告 ID 以及內容溢出的公告 ID 集合
  const [announcementList, setAnnouncementList] = useState([]);
  // 用於存儲公告內容的 ref，以便檢測內容是否溢出
  const [expendedId, setExpendedId] = useState(null);
  // 用於存儲內容溢出的公告 ID，使用 Set 以避免重複
  const [overflowIds, setOverflowIds] = useState(new Set());
  // 使用 useRef 來存儲每個公告內容的 DOM 元素，以便後續檢測內容是否溢出
  const contentRefs = useRef({});

  // 切換公告內容的展開與收起狀態
  const toggleExpand = (id) => {
    setExpendedId((prev) => (prev === id ? null : id));
  };

  // 當 redux 中的公告列表發生變化時，根據當前語言處理公告列表並更新本地狀態
  useEffect(() => {
    const list = getList(reduxAnnouncementList.list);
    setAnnouncementList(list);
  }, [reduxAnnouncementList]);

  // 當公告列表發生變化時，檢測每個公告內容是否溢出，並更新 overflowIds 狀態
  // 注入的 HTML 內含圖片時高度為非同步決定，因此另外以 ResizeObserver 重新量測
  useEffect(() => {
    const measure = () => {
      setOverflowIds((prev) => {
        const next = new Set(prev);
        let changed = false;
        announcementList.forEach((announcement) => {
          const uid = announcement.currentLang.uid;
          const element = contentRefs.current[uid];
          // 展開時 scrollHeight 會等於 clientHeight，此時維持既有結果避免箭頭消失
          if (!element || expendedId === uid) return;
          const isOverflow = element.scrollHeight > element.clientHeight;
          if (isOverflow !== next.has(uid)) {
            changed = true;
            isOverflow ? next.add(uid) : next.delete(uid);
          }
        });
        return changed ? next : prev;
      });
    };

    measure();

    const observer = new ResizeObserver(measure);
    const cleanups = [];
    announcementList.forEach((announcement) => {
      const element = contentRefs.current[announcement.currentLang.uid];
      if (!element) return;
      observer.observe(element);
      // 收合狀態下高度被 maxHeight 鎖住，ResizeObserver 不會觸發，改由圖片載入事件補量測
      element.addEventListener('load', measure, true);
      element.addEventListener('error', measure, true);
      cleanups.push(() => {
        element.removeEventListener('load', measure, true);
        element.removeEventListener('error', measure, true);
      });
    });

    return () => {
      observer.disconnect();
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [announcementList, expendedId]);

  if (!announcementList || announcementList.length === 0) {
    return (
      <Box className="w-full p-4 text-center">
        <Text>{t('announcement.no_announcement')}</Text>
      </Box>
    );
  }

  return (
    <Box
      className="space-y-4"
      paddingBottom={'6em'}
      overflow={'scroll'}
      height={'100%'}
    >
      {announcementList?.map((data) => {
        const currentAnnouncement = data?.currentLang;
        const isExpended = expendedId === currentAnnouncement.uid;
        const hasOverflow = overflowIds.has(currentAnnouncement.uid);
        return (
          <Box
            key={currentAnnouncement.uid}
            bgSize="100% 100%"
            height={'fit-content'}
            border={'2px solid #A4F0FF'}
          >
            <Box
              display={'flex'}
              justifyContent={hasOverflow ? 'space-between' : 'flex-start'}
              alignItems={'center'}
              padding={'0.5em 1em'}
              width={'100%'}
              backgroundColor={'#003249CC'}
              borderBottom={'2px solid #A4F0FF'}
            >
              <Text fontSize="1.2em">{currentAnnouncement.title}</Text>
              {hasOverflow && (
                <Box
                  cursor={'pointer'}
                  display={'flex'}
                  justifyContent={'center'}
                  height={'2em'}
                  _hover={{
                    transform: 'scale(1.1)',
                    transition: 'transform 0.2s'
                  }}
                  onClick={() => toggleExpand(currentAnnouncement.uid)}
                >
                  <Image
                    src={belowIcon}
                    transform={isExpended ? 'rotate(90deg)' : 'rotate(0deg)'}
                    transition="transform 0.3s ease"
                  />
                </Box>
              )}
            </Box>
            <Box padding={'0.5em 1em'} backgroundColor={'#011929'}>
              <Box
                ref={(element) =>
                  (contentRefs.current[currentAnnouncement.uid] = element)
                }
                fontWeight={'thin'}
                overflow={isExpended ? 'visible' : 'hidden'}
                maxHeight={isExpended ? 'none' : COLLAPSED_MAX_HEIGHT}
                sx={richTextStyle}
                className="break-words jodit-wysiwyg"
                dangerouslySetInnerHTML={{
                  __html: currentAnnouncement.content
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default AnnouncementContent;
